import React, { useEffect, useState, useRef } from 'react';
import { useMessages, Message } from './MessageContext';
import { AlertCircleIcon, SendIcon, MicIcon, StopCircleIcon } from 'lucide-react';
interface MessageThreadProps {
  conversationId: string;
}
export const MessageThread: React.FC<MessageThreadProps> = ({
  conversationId
}) => {
  const {
    messages,
    sendMessage,
    conversations,
    markConversationAsRead
  } = useMessages();
  const [newMessage, setNewMessage] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const conversation = conversations.find(c => c.id === conversationId);
  const threadMessages = messages[conversationId] || [];
  // Speech recognition setup
  const recognition = useRef<SpeechRecognition | null>(null);
  // Initialize speech recognition
  useEffect(() => {
    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognition.current = new SpeechRecognition();
      recognition.current.continuous = true;
      recognition.current.interimResults = true;
      recognition.current.onresult = event => {
        const transcript = Array.from(event.results).map(result => result[0].transcript).join('');
        setNewMessage(transcript);
        // Focus and move cursor to end of textarea
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.selectionStart = textareaRef.current.value.length;
          textareaRef.current.selectionEnd = textareaRef.current.value.length;
        }
      };
      recognition.current.onerror = event => {
        console.error('Speech recognition error', event.error);
        setRecordingError('Error with speech recognition. Please try again.');
        setIsRecording(false);
      };
      recognition.current.onend = () => {
        setIsRecording(false);
      };
    }
    return () => {
      if (recognition.current) {
        recognition.current.onresult = null;
        recognition.current.onend = null;
        recognition.current.onerror = null;
        if (isRecording) {
          recognition.current.stop();
        }
      }
    };
  }, []);
  // Mark conversation as read when opened
  useEffect(() => {
    if (conversationId) {
      markConversationAsRead(conversationId);
    }
  }, [conversationId, markConversationAsRead]);
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [threadMessages]);
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversation) return;
    sendMessage(conversation.participantId, newMessage, conversation.jobId, isUrgent);
    setNewMessage('');
    setIsUrgent(false);
  };
  const toggleRecording = () => {
    if (!recognition.current) {
      setRecordingError('Speech recognition is not supported in your browser.');
      return;
    }
    setRecordingError('');
    if (isRecording) {
      recognition.current.stop();
      setIsRecording(false);
    } else {
      try {
        recognition.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        setRecordingError('Could not start recording. Please try again.');
      }
    }
  };
  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <>
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50" data-id="element-360">
        {threadMessages.length === 0 ? <div className="h-full flex items-center justify-center text-gray-500" data-id="element-361">
            <p data-id="element-362">No messages yet. Start the conversation!</p>
          </div> : <div className="space-y-3" data-id="element-363">
            {threadMessages.map(message => {
          const isFromUser = message.senderId !== conversation?.participantId;
          return <div key={message.id} className={`flex ${isFromUser ? 'justify-end' : 'justify-start'}`} data-id="element-364">
                  <div className={`max-w-[80%] rounded-lg p-3 ${isFromUser ? 'bg-[#0F2A52] text-white rounded-br-none' : 'bg-white border rounded-bl-none'} ${message.urgent ? 'border-[#F58220]' : ''}`} data-id="element-365">
                    {message.urgent && <div className={`flex items-center gap-1 mb-1 ${isFromUser ? 'text-[#F58220]/80' : 'text-[#F58220]'}`} data-id="element-366">
                        <AlertCircleIcon size={14} data-id="element-367" />
                        <span className="text-xs font-medium" data-id="element-368">Urgent</span>
                      </div>}
                    <p className="whitespace-pre-wrap" data-id="element-369">{message.content}</p>
                    <div className={`text-xs mt-1 ${isFromUser ? 'text-blue-200' : 'text-gray-500'}`} data-id="element-370">
                      {formatMessageTime(message.timestamp)}
                    </div>
                  </div>
                </div>;
        })}
            <div ref={messagesEndRef} data-id="element-371" />
          </div>}
      </div>
      {/* Message Input */}
      <div className="p-3 border-t bg-white" data-id="element-372">
        <form onSubmit={handleSendMessage} className="flex flex-col" data-id="element-373">
          <div className="flex items-center mb-2" data-id="element-374">
            <label className="flex items-center text-sm cursor-pointer" data-id="element-375">
              <input type="checkbox" checked={isUrgent} onChange={() => setIsUrgent(!isUrgent)} className="mr-2 h-4 w-4 text-[#F58220] border-gray-300 rounded focus:ring-[#F58220]" data-id="element-376" />
              <AlertCircleIcon size={14} className="text-[#F58220] mr-1" data-id="element-377" />
              Mark as urgent
            </label>
          </div>
          {recordingError && <div className="text-red-500 text-xs mb-2" data-id="element-378">{recordingError}</div>}
          <div className="flex gap-2" data-id="element-379">
            <textarea ref={textareaRef} value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type your message..." className={`flex-1 border rounded-md p-2 resize-none focus:border-[#0F2A52] focus:ring-1 focus:ring-[#0F2A52] outline-none ${isRecording ? 'border-[#F58220] bg-[#F58220]/5' : ''}`} rows={2} onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e);
            }
          }} data-id="element-380" />
            <div className="flex flex-col gap-2 self-end" data-id="element-381">
              <button type="button" onClick={toggleRecording} className={`p-2 rounded-md transition-colors ${isRecording ? 'bg-[#F58220] text-white hover:bg-[#F58220]/90' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} aria-label={isRecording ? 'Stop recording' : 'Start voice input'} title={isRecording ? 'Stop recording' : 'Start voice input'} data-id="element-382">
                {isRecording ? <StopCircleIcon size={18} data-id="element-383" /> : <MicIcon size={18} data-id="element-384" />}
              </button>
              <button type="submit" disabled={!newMessage.trim()} className="bg-[#0F2A52] text-white rounded-md px-4 h-10 flex items-center justify-center disabled:bg-[#0F2A52]/50 transition-colors hover:bg-[#0F2A52]/90" data-id="element-385">
                <SendIcon size={18} data-id="element-386" />
              </button>
            </div>
          </div>
          {isRecording && <div className="flex items-center gap-2 mt-2 text-[#F58220] text-xs animate-pulse" data-id="element-387">
              <span className="inline-block w-2 h-2 bg-[#F58220] rounded-full" data-id="element-388"></span>
              Recording... Speak now
            </div>}
        </form>
      </div>
    </>;
};